function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t["return"] && (u = t["return"](), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
import { Popover } from '@hypothesis/frontend-shared';
import classnames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'preact/hooks';
import MentionPopoverContent from '../components/MentionPopoverContent';
import StyledText from '../components/StyledText';
import { processAndReplaceMentionElements } from '../helpers';
import { renderMathAndMarkdown } from '../utils';
import { replaceLinksWithEmbeds } from '../utils/media-embedder';

/** Return true if the point (x, y) lies within `rect`. */
import { jsx as _jsx, jsxs as _jsxs } from "preact/jsx-runtime";
function rectContainsPoint(rect, x, y) {
  return x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
}

/**
 * Return the smallest rect that entirely contains `rect` and has integer
 * coordinates.
 */
function roundRectCoords(rect) {
  var left = Math.floor(rect.x);
  var top = Math.floor(rect.y);
  var right = Math.ceil(rect.right);
  var bottom = Math.ceil(rect.bottom);
  return new DOMRect(left, top, right - left, bottom - top);
}

/** Return the smallest rect which contains both `a` and `b`. */
function unionRect(a, b) {
  var left = Math.min(a.x, b.x);
  var top = Math.min(a.y, b.y);
  var right = Math.max(a.right, b.right);
  var bottom = Math.max(a.bottom, b.bottom);
  return DOMRect.fromRect({
    x: left,
    y: top,
    width: right - left,
    height: bottom - top
  });
}
/**
 * A component which renders markdown as HTML, replaces recognized links with
 * embedded video/audio and processes mention tags.
 */
export default function MarkdownView(props) {
  /* istanbul ignore next - Unpack here to ignore default values for test seams */
  var markdown = props.markdown,
    classes = props.classes,
    style = props.style,
    _props$mentions = props.mentions,
    mentions = _props$mentions === void 0 ? [] : _props$mentions,
    _props$mentionsEnable = props.mentionsEnabled,
    mentionsEnabled = _props$mentionsEnable === void 0 ? false : _props$mentionsEnable,
    mentionMode = props.mentionMode,
    _props$setTimeout_ = props.setTimeout_,
    setTimeout_ = _props$setTimeout_ === void 0 ? setTimeout : _props$setTimeout_,
    _props$clearTimeout_ = props.clearTimeout_,
    clearTimeout_ = _props$clearTimeout_ === void 0 ? clearTimeout : _props$clearTimeout_;
  var html = useMemo(function () {
    return markdown ? renderMathAndMarkdown(markdown) : '';
  }, [markdown]);
  var content = useRef(null);
  var mentionsPopoverAnchorRef = useRef(null);
  var mentionsPopoverRef = useRef(null);
  var elementToMentionMap = useRef(new Map());
  var _useState = useState(null),
    _useState2 = _slicedToArray(_useState, 2),
    popoverContent = _useState2[0],
    setPopoverContent = _useState2[1];
  var popoverContentTimeout = useRef();
  var setPopoverContentAfterDelay = useCallback(
  // This allows the content to be set with a small delay, so that popovers
  // don't flicker simply by hovering an annotation with mentions
  function (content) {
    if (popoverContentTimeout.current) {
      clearTimeout_(popoverContentTimeout.current);
    }
    var setContent = function setContent() {
      setPopoverContent(content);
      popoverContentTimeout.current = null;
    };

    // Set the content immediately when resetting, so that there's no delay
    // when hiding the popover, only when showing it
    if (content === null) {
      setContent();
    } else {
      popoverContentTimeout.current = setTimeout_(setContent, 400);
    }
  }, [clearTimeout_, setTimeout_]);
  useEffect(function () {
    // Clear any potentially in-progress popover timeout when this component is
    // unmounted
    return function () {
      if (popoverContentTimeout.current) {
        clearTimeout_(popoverContentTimeout.current);
      }
    };
  }, [clearTimeout_]);
  useEffect(function () {
    replaceLinksWithEmbeds(content.current, {
      // Make embeds the full width of the sidebar, unless the sidebar has been
      // made wider than the `md` breakpoint. In that case, restrict width
      // to 380px.
      className: 'w-full md:w-[380px]'
    });
  }, [markdown]);
  useEffect(function () {
    elementToMentionMap.current = processAndReplaceMentionElements(content.current, mentions, mentionMode);
  }, [mentionMode, mentions]);

  // Monitor mouse position when mentions popover is visible and hide when it
  // goes outside the anchor or popover.
  useLayoutEffect(function () {
    var anchor = mentionsPopoverAnchorRef.current;
    var popover = mentionsPopoverRef.current;
    if (!anchor || !popover || !popoverContent) {
      return function () {};
    }
    var maybeHidePopover = function maybeHidePopover(e) {
      // Element boxes may have fractional coordinates, but mouse event
      // coordinates are integers (in Chrome at least). Expand the boxes to the
      // smallest box with integer coordinates. This way a mouse event
      // dispatched at the exact corner of one of the boxes will still be deemed
      // "inside".
      var anchorBox = roundRectCoords(anchor.getBoundingClientRect());
      var popoverBox = roundRectCoords(popover.getBoundingClientRect());

      // There may be a small gap between the anchor and popover. To avoid
      // hiding the popover when the mouse moves across this gap, we only hide
      // the popover when it moves outside the union of these two boxes.
      var unionBox = unionRect(anchorBox, popoverBox);
      if (!rectContainsPoint(unionBox, e.clientX, e.clientY)) {
        setPopoverContentAfterDelay(null);
        mentionsPopoverAnchorRef.current = null;
      }
    };

    // Use a listener on the body because there isn't a single element that
    // corresponds to the region which the mouse has to exit before we hide the
    // popover.
    document.body.addEventListener('mousemove', maybeHidePopover);
    return function () {
      document.body.removeEventListener('mousemove', maybeHidePopover);
    };
  }, [popoverContent, setPopoverContentAfterDelay]);

  // NB: The following could be implemented by setting attribute props directly
  // on `StyledText` (which renders a `div` itself), versus introducing a child
  // `div` as is done here. However, in initial testing, this interfered with
  // some overflow calculations in the `Excerpt` element. This could be worth
  // a review in the future.
  return _jsxs("div", {
    className: classnames('w-full wrap-anywhere cursor-text', {
      // A `relative` wrapper around the `Popover` component is needed for
      // when the native Popover API is not supported.
      relative: mentionsEnabled
    }),
    children: [_jsx(StyledText, {
      children: _jsx("div", {
        className: classes,
        "data-testid": "markdown-text",
        ref: content,
        dangerouslySetInnerHTML: {
          __html: html
        },
        style: style,
        onMouseEnterCapture: mentionsEnabled ? function (_ref) {
          var target = _ref.target;
          var element = target;
          var mention = elementToMentionMap.current.get(element);
          if (mention) {
            setPopoverContentAfterDelay(mention);
            mentionsPopoverAnchorRef.current = element;
          }
        } : undefined,
        onMouseLeaveCapture: function onMouseLeaveCapture(_ref2) {
          var target = _ref2.target;
          // If the mouse leaves the mention before the popover has been
          // shown, cancel the timer that shows the popover.
          //
          // Once the popover is shown, hiding it is handled by a separate
          // effect that fires once the mouse leaves the area containing both
          // the popover and the anchor.
          var element = target;
          if (mentionsPopoverAnchorRef.current === element && !popoverContent) {
            setPopoverContentAfterDelay(null);
            mentionsPopoverAnchorRef.current = null;
          }
        }
      })
    }), mentionsEnabled && _jsx(Popover, {
      open: !!popoverContent,
      onClose: function onClose() {
        return setPopoverContentAfterDelay(null);
      },
      anchorElementRef: mentionsPopoverAnchorRef,
      classes: "!max-w-[75%]",
      elementRef: mentionsPopoverRef,
      children: _jsx(MentionPopoverContent, {
        content: popoverContent !== null && popoverContent !== void 0 ? popoverContent : '',
        mentionMode: mentionMode
      })
    })]
  });
}
//# sourceMappingURL=MarkdownView.js.map